import React, { useState, useRef, useEffect } from 'react';
import { Target } from 'lucide-react';
import { Tribe, Objective, KeyResult } from '../data';
import { UpdateKR } from '../hooks/useOrgState';
import { useAuth } from '../context/AuthContext';

interface Props {
  tribe: Tribe;
  cycle: string;
  updateKR: UpdateKR;
}

interface Editing {
  objectiveId: string;
  krId: string;
}

function krProgress(kr: KeyResult): number {
  if (kr.lowerIsBetter) {
    if (kr.current <= kr.target) return 100;
    return Math.min(Math.round((kr.target / kr.current) * 100), 100);
  }
  return Math.min(Math.round((kr.current / kr.target) * 100), 100);
}

function objProgress(obj: Objective): number {
  if (!obj.keyResults.length) return 0;
  const sum = obj.keyResults.reduce((acc, kr) => acc + krProgress(kr), 0);
  return Math.round(sum / obj.keyResults.length);
}

function progressStatus(pct: number): 'ok' | 'risk' | 'behind' {
  if (pct >= 70) return 'ok';
  if (pct >= 40) return 'risk';
  return 'behind';
}

function formatValue(val: number): string {
  return Number.isInteger(val) ? String(val) : val.toFixed(1);
}

export default function OKRSection({ tribe, cycle, updateKR }: Props) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('editor');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function startEdit(objectiveId: string, kr: KeyResult) {
    setEditing({ objectiveId, krId: kr.id });
    setDraft(String(kr.current));
  }

  function commitEdit(objectiveId: string, krId: string) {
    const val = parseFloat(draft.replace(',', '.'));
    if (!isNaN(val) && val >= 0) {
      updateKR(tribe.id, objectiveId, krId, val);
    }
    setEditing(null);
  }

  return (
    <section className="tribe-section">
      <div className="section-header">
        <Target size={16} />
        <h2>OKRs</h2>
        <span className="section-badge okr-cycle-badge">{cycle}</span>
      </div>

      <div className="okr-list">
        {(tribe.okrs ?? []).map((obj, oi) => {
          const opct = objProgress(obj);
          const ostatus = progressStatus(opct);

          return (
            <div key={obj.id} className="okr-objective">
              <div className="okr-obj-header">
                <span className="okr-obj-index">O{oi + 1}</span>
                <span className="okr-obj-title">{obj.title}</span>
                <div className="okr-obj-summary">
                  <div className="okr-obj-mini-bar-wrap">
                    <div
                      className={`okr-obj-mini-bar okr-bar-${ostatus}`}
                      style={{ width: `${opct}%` }}
                    />
                  </div>
                  <span className={`okr-obj-pct okr-status-${ostatus}`}>{opct}%</span>
                </div>
              </div>

              <div className="okr-krs">
                {obj.keyResults.map((kr, ki) => {
                  const kpct = krProgress(kr);
                  const kstatus = progressStatus(kpct);
                  const isEditing =
                    editing?.objectiveId === obj.id && editing.krId === kr.id;

                  return (
                    <div key={kr.id} className="okr-kr">
                      <div className="okr-kr-top">
                        <span className="okr-kr-index">KR{ki + 1}</span>
                        <span className="okr-kr-desc">{kr.description}</span>
                        <div className="okr-kr-value">
                          {(isEditing && canEdit) ? (
                            <input
                              ref={inputRef}
                              className="okr-kr-input"
                              value={draft}
                              onChange={e => setDraft(e.target.value)}
                              onBlur={() => commitEdit(obj.id, kr.id)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') commitEdit(obj.id, kr.id);
                                if (e.key === 'Escape') setEditing(null);
                              }}
                            />
                          ) : (
                            <span
                              className={`okr-kr-current${canEdit ? '' : ' okr-kr-readonly'}`}
                              onClick={canEdit ? () => startEdit(obj.id, kr) : undefined}
                              title={canEdit ? 'Clique para atualizar' : undefined}
                            >
                              {formatValue(kr.current)}
                            </span>
                          )}
                          <span className="okr-kr-sep"> / </span>
                          <span className="okr-kr-target">{formatValue(kr.target)}</span>
                          {kr.unit && (
                            <span className="okr-kr-unit"> {kr.unit}</span>
                          )}
                          {kr.lowerIsBetter && (
                            <span className="okr-kr-hint" title="Menor é melhor"> ↓</span>
                          )}
                        </div>
                      </div>

                      <div className="okr-kr-bar-row">
                        <div className="okr-kr-bar-wrap">
                          <div
                            className={`okr-kr-bar okr-bar-${kstatus}`}
                            style={{ width: `${kpct}%` }}
                          />
                        </div>
                        <span className={`okr-kr-pct okr-status-${kstatus}`}>
                          {kpct}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
